/*  ========================================================================
    # Components - Booking Widget
    ========================================================================  */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useBookingUrl from '../../hooks/useBookingUrl';
import bem from '../../utils/bem';
import NumericInput from '../inputs/NumericInput';
import DateInput from '../inputs/DateInput';
import moment from 'moment';
import useTranslation from '../../hooks/useTranslation';

const BookingWidget = ({
  variant,
  max_people,
  booking_url,
  button_text,
  open_new_tab,
  culture = 'de-DE'
}) => {
  const __ = useTranslation();
  const [arrivalDate, setArrivalDate] = useState(undefined);
  const [departureDate, setDepartureDate] = useState(undefined);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);

  const isOverbooked = adults + children > max_people;

  // Hook into new arrival date and clear departure date, if necessary
  const handleNewArrivalDate = date => {
    if (date >= departureDate) {
      setDepartureDate(undefined);
    }
    setArrivalDate(date);
  };

  const fullBookingUrl = useBookingUrl(
    booking_url,
    arrivalDate,
    departureDate,
    adults,
    children,
    culture
  );

  const datePickerLocale = culture?.substring(0, 2) || 'en';

  return (
    <div
      className={bem('c-booking-widget', 'app', {
        full: variant === 'full',
        'dates-only': variant === 'dates-only'
      })}
    >
      <DateInput
        currentValue={arrivalDate}
        onValueChanged={handleNewArrivalDate}
        min={new Date()}
        label={__('arrival')}
        name='arrival'
        startDate={arrivalDate}
        endDate={departureDate}
        selects='arrival'
        locale={datePickerLocale}
      />
      <DateInput
        currentValue={departureDate}
        onValueChanged={setDepartureDate}
        min={moment(arrivalDate).toDate()}
        label={__('departure')}
        name='departure'
        startDate={arrivalDate}
        endDate={departureDate}
        selects='departure'
        locale={datePickerLocale}
      />
      {variant === 'full' && (
        <>
          <NumericInput
            currentValue={adults}
            onValueChanged={setAdults}
            min={0}
            label={__('adults')}
            name='adults'
          />
          <NumericInput
            currentValue={children}
            onValueChanged={setChildren}
            min={0}
            label={__('children')}
            name='children'
          />
        </>
      )}

      <div className='c-booking-widget__submit'>
        {isOverbooked && (
          <div className='c-booking-widget__overbooked'>
            <p className='c-booking-widget__overbooked-notice'>
              <b>{__('overbooked_title')}</b>
              <br />
              {__('overbooked_text')}
            </p>
          </div>
        )}
        <a
          className={bem(
            'c-booking-widget',
            'button',
            {
              hidden: isOverbooked
            },
            [
              'c-button',
              'c-button--small',
              isOverbooked || adults + children < 1 ? 'c-button--disabled' : ''
            ]
          )}
          target={open_new_tab ? '_blank' : '_self'}
          href={fullBookingUrl}
          rel='noreferrer'
        >
          {button_text}
        </a>
      </div>
    </div>
  );
};

BookingWidget.propTypes = {
  variant: PropTypes.oneOf(['full', 'dates-only']).isRequired,
  max_people: PropTypes.number.isRequired,
  booking_url: PropTypes.string.isRequired,
  button_text: PropTypes.string.isRequired,
  open_new_tab: PropTypes.bool,
  culture: PropTypes.string
};

export default BookingWidget;
